import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Search, Filter, Crown, Target, Calendar, Star, Activity, Heart } from 'lucide-react';
import { ProgramCard } from './ProgramCard';
import { ProgramDetail } from './ProgramDetail';
import { workoutPrograms } from '../../data/workoutPrograms';

export function ProgramsTab() {
  const [selectedProgram, setSelectedProgram] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedLevel, setSelectedLevel] = useState<string>('All');
  const [selectedDuration, setSelectedDuration] = useState<string>('All');

  const levels = ['All', 'Beginner', 'Intermediate', 'Advanced', 'Elite'];
  const durations = ['All', '4 weeks', '8 weeks', '12 weeks', 'Ongoing'];

  const filteredPrograms = workoutPrograms.filter(program => {
    const matchesSearch = program.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         program.description.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesLevel = selectedLevel === 'All' || program.level === selectedLevel;
    const matchesDuration = selectedDuration === 'All' || program.duration === selectedDuration;
    return matchesSearch && matchesLevel && matchesDuration;
  });

  // Separate programs by category
  const featuredPrograms = filteredPrograms.filter(p => p.level === 'Elite');
  const strengthPrograms = filteredPrograms.filter(p => p.category === 'Strength' && p.level !== 'Elite');
  const cardioPrograms = filteredPrograms.filter(p => p.category === 'Cardio' && p.level !== 'Elite');
  const mixedPrograms = filteredPrograms.filter(p => p.category === 'Mixed' && p.level !== 'Elite');

  const selectedProgramData = workoutPrograms.find(p => p.id === selectedProgram);

  if (selectedProgramData) {
    return (
      <ProgramDetail
        program={selectedProgramData}
        onClose={() => setSelectedProgram(null)}
      />
    );
  }

  return (
    <div className="space-y-6">
      {/* Search and Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="flex-1 relative">
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search programs..."
            className="w-full bg-muted/50 border border-border/50 rounded-lg pl-10 pr-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
          />
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        </div>
        
        <div className="flex gap-2">
          <select
            value={selectedLevel}
            onChange={(e) => setSelectedLevel(e.target.value)}
            className="bg-muted/50 border border-border/50 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
          >
            {levels.map(level => (
              <option key={level} value={level}>{level}</option>
            ))}
          </select>
          
          <select
            value={selectedDuration}
            onChange={(e) => setSelectedDuration(e.target.value)}
            className="bg-muted/50 border border-border/50 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
          >
            {durations.map(duration => (
              <option key={duration} value={duration}>{duration}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Featured Programs */}
      {featuredPrograms.length > 0 && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Crown className="w-5 h-5 text-amber-500" />
              <h2 className="text-lg font-semibold">Featured Programs</h2>
            </div>
            <div className="text-sm text-amber-500">Elite training experiences</div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {featuredPrograms.map(program => (
              <ProgramCard
                key={program.id}
                program={program}
                onSelect={setSelectedProgram}
              />
            ))}
          </div>
        </div>
      )}

      {/* Strength Programs */}
      {strengthPrograms.length > 0 && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Target className="w-5 h-5 text-primary" />
              <h2 className="text-lg font-semibold">Strength Programs</h2>
            </div>
            <div className="text-sm text-primary">Build strength and power</div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {strengthPrograms.map(program => (
              <ProgramCard
                key={program.id}
                program={program}
                onSelect={setSelectedProgram}
              />
            ))}
          </div>
        </div>
      )}

      {/* Cardio Programs */}
      {cardioPrograms.length > 0 && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Heart className="w-5 h-5 text-primary" />
              <h2 className="text-lg font-semibold">Cardio Programs</h2>
            </div>
            <div className="text-sm text-primary">Boost endurance and stamina</div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {cardioPrograms.map(program => (
              <ProgramCard
                key={program.id}
                program={program}
                onSelect={setSelectedProgram}
              />
            ))}
          </div>
        </div>
      )}

      {/* Mixed Programs */}
      {mixedPrograms.length > 0 && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Activity className="w-5 h-5 text-primary" />
              <h2 className="text-lg font-semibold">Mixed Programs</h2>
            </div>
            <div className="text-sm text-primary">Dynamic training variety</div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {mixedPrograms.map(program => (
              <ProgramCard
                key={program.id}
                program={program}
                onSelect={setSelectedProgram}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}